import { MESES } from '@/features/catalog/catalog';
import { currentPeriod, usePeriod } from './PeriodProvider';

/**
 * Elegir el mes que se está mirando.
 *
 * Antes era sólo `‹ mes ›`. Para septiembre desde febrero había que tocar siete
 * veces, mirando el rótulo cada vez para saber si ya se había pasado. Y en
 * Liquidación directamente no estaba: había que volver al resumen, mover el mes
 * y entrar de nuevo.
 *
 * Las flechas se quedan, porque el mes de al lado es lo que más se consulta y
 * un toque es más rápido que abrir un desplegable. Lo que se agrega es poder
 * saltar a cualquier mes, y volver al actual cuando uno se perdió.
 */
export function SelectorPeriodo({ testId = 'periodo' }: { testId?: string }) {
  const { year, month, setPeriod, shiftMonth } = usePeriod();
  const hoy = currentPeriod();
  const esActual = year === hoy.year && month === hoy.month;

  // Cuatro años hacia atrás y uno hacia adelante: alcanza para consultar
  // histórico y para facturar un trabajo a un mes que todavía no llegó.
  const anios = [hoy.year + 1, hoy.year, hoy.year - 1, hoy.year - 2, hoy.year - 3];

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          aria-label="Mes anterior"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
          data-testid={`${testId}-anterior`}
        >
          ‹
        </button>

        <select
          value={month}
          onChange={(e) => setPeriod({ year, month: Number(e.target.value) })}
          aria-label="Mes"
          className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
          data-testid={`${testId}-mes`}
        >
          {MESES.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => setPeriod({ year: Number(e.target.value), month })}
          aria-label="Año"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
          data-testid={`${testId}-anio`}
        >
          {anios.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => shiftMonth(1)}
          aria-label="Mes siguiente"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
          data-testid={`${testId}-siguiente`}
        >
          ›
        </button>
      </div>

      {/* Sólo cuando hace falta: un atajo que siempre está visible deja de
          leerse, y acá lo que importa es notar que uno NO está en el mes
          corriente. */}
      {!esActual && (
        <button
          type="button"
          onClick={() => setPeriod(hoy)}
          className="min-h-11 self-start text-sm underline opacity-70"
          data-testid={`${testId}-hoy`}
        >
          Volver a {MESES[hoy.month - 1]} {hoy.year}
        </button>
      )}
    </div>
  );
}
